import { http } from "@/lib/http";

import type {
  Bucket,
  BucketCors,
  BucketCorsRule,
  BucketCreateRequest,
  BucketLifecycle,
  BucketLifecycleUpdateRequest,
  BucketObjectLock,
  BucketObjectLockUpdateRequest,
  BucketPolicy,
  BucketPolicyUpdateRequest,
  BucketVersioning,
  BucketVersioningUpdateRequest,
  CreateFolderRequest,
  EmptyBucketResult,
  FolderDeleteResult,
  MultipartCompleteRequest,
  MultipartCompleteResult,
  MultipartInitRequest,
  MultipartUpload,
  ObjectCopyRequest,
  ObjectItem,
  ObjectLegalHoldRequest,
  ObjectRetentionRequest,
  ObjectVersion,
  PresignedUrl,
  PresignRequest,
  BucketEventRule,
  BucketEventRuleCreateRequest,
  BucketEventRuleUpdateRequest,
  BucketEventRules,
  BucketEventTestResult,
} from "./types";

// Object keys are user-named and may contain `?`, `#`, `%`, spaces, …;
// encode each path segment so they survive the URL. Slashes must stay —
// the backend route is `{key:path}`. Buckets get plain encodeURIComponent.
const encodeKey = (key: string) =>
  key.split("/").map(encodeURIComponent).join("/");

export async function listBuckets(): Promise<Bucket[]> {
  const { data } = await http.get<Bucket[]>("/objects/buckets");
  return data;
}

export async function createBucket(
  payload: BucketCreateRequest,
): Promise<Bucket> {
  const { data } = await http.post<Bucket>("/objects/buckets", payload);
  return data;
}

export async function deleteBucket(bucket: string): Promise<void> {
  await http.delete(`/objects/buckets/${encodeURIComponent(bucket)}`);
}

/**
 * Purge everything in a bucket — current objects, old versions and
 * delete-markers. The version-aware path a versioning-enabled bucket needs
 * before it can be deleted (plain object deletes leave versions behind).
 */
export async function emptyBucket(
  bucket: string,
): Promise<EmptyBucketResult> {
  const { data } = await http.post<EmptyBucketResult>(
    `/objects/buckets/${encodeURIComponent(bucket)}/empty`,
  );
  return data;
}

export async function listObjects(bucket: string): Promise<ObjectItem[]> {
  const { data } = await http.get<ObjectItem[]>(
    `/objects/buckets/${encodeURIComponent(bucket)}/objects`,
  );
  return data;
}

export async function uploadObject(
  bucket: string,
  file: File,
  prefix = "",
): Promise<ObjectItem> {
  const form = new FormData();
  form.append("file", file);
  const { data } = await http.post<ObjectItem>(
    `/objects/buckets/${encodeURIComponent(bucket)}/objects`,
    form,
    {
      headers: { "Content-Type": "multipart/form-data" },
      params: prefix ? { prefix } : {},
    },
  );
  return data;
}

export async function createFolder(
  bucket: string,
  payload: CreateFolderRequest,
): Promise<ObjectItem> {
  const { data } = await http.post<ObjectItem>(
    `/objects/buckets/${encodeURIComponent(bucket)}/folders`,
    payload,
  );
  return data;
}

/** Recursively delete a folder prefix — every object under it, marker included. */
export async function deleteFolder(
  bucket: string,
  prefix: string,
): Promise<FolderDeleteResult> {
  const { data } = await http.delete<FolderDeleteResult>(
    `/objects/buckets/${encodeURIComponent(bucket)}/folders`,
    { params: { prefix } },
  );
  return data;
}

export async function deleteObject(
  bucket: string,
  key: string,
): Promise<void> {
  await http.delete(`/objects/buckets/${encodeURIComponent(bucket)}/objects/${encodeKey(key)}`);
}

export async function copyObject(
  bucket: string,
  key: string,
  payload: ObjectCopyRequest,
): Promise<ObjectItem> {
  const { data } = await http.post<ObjectItem>(
    `/objects/buckets/${encodeURIComponent(bucket)}/objects/${encodeKey(key)}/copy`,
    payload,
  );
  return data;
}

export async function getBucketVersioning(
  bucket: string,
): Promise<BucketVersioning> {
  const { data } = await http.get<BucketVersioning>(
    `/objects/buckets/${encodeURIComponent(bucket)}/versioning`,
  );
  return data;
}

export async function setBucketVersioning(
  bucket: string,
  payload: BucketVersioningUpdateRequest,
): Promise<BucketVersioning> {
  const { data } = await http.put<BucketVersioning>(
    `/objects/buckets/${encodeURIComponent(bucket)}/versioning`,
    payload,
  );
  return data;
}

export async function getBucketLifecycle(
  bucket: string,
): Promise<BucketLifecycle> {
  const { data } = await http.get<BucketLifecycle>(
    `/objects/buckets/${encodeURIComponent(bucket)}/lifecycle`,
  );
  return data;
}

export async function setBucketLifecycle(
  bucket: string,
  payload: BucketLifecycleUpdateRequest,
): Promise<BucketLifecycle> {
  const { data } = await http.put<BucketLifecycle>(
    `/objects/buckets/${encodeURIComponent(bucket)}/lifecycle`,
    payload,
  );
  return data;
}

// --- Access policy and object lock ---
// The product writes these with its own account after its own checks; an
// access key is refused the same writes over S3.

export async function getBucketPolicy(bucket: string): Promise<BucketPolicy> {
  const { data } = await http.get<BucketPolicy>(
    `/objects/buckets/${encodeURIComponent(bucket)}/policy`,
  );
  return data;
}

export async function setBucketPolicy(
  bucket: string,
  payload: BucketPolicyUpdateRequest,
): Promise<BucketPolicy> {
  const { data } = await http.put<BucketPolicy>(
    `/objects/buckets/${encodeURIComponent(bucket)}/policy`,
    payload,
  );
  return data;
}

export async function deleteBucketPolicy(bucket: string): Promise<void> {
  await http.delete(`/objects/buckets/${encodeURIComponent(bucket)}/policy`);
}

export async function getBucketObjectLock(
  bucket: string,
): Promise<BucketObjectLock> {
  const { data } = await http.get<BucketObjectLock>(
    `/objects/buckets/${encodeURIComponent(bucket)}/object-lock`,
  );
  return data;
}

export async function setBucketObjectLock(
  bucket: string,
  payload: BucketObjectLockUpdateRequest,
): Promise<BucketObjectLock> {
  const { data } = await http.put<BucketObjectLock>(
    `/objects/buckets/${encodeURIComponent(bucket)}/object-lock`,
    payload,
  );
  return data;
}

export async function listObjectVersions(
  bucket: string,
  key: string,
): Promise<ObjectVersion[]> {
  const { data } = await http.get<ObjectVersion[]>(
    `/objects/buckets/${encodeURIComponent(bucket)}/versions`,
    { params: { key } },
  );
  return data;
}

export async function setObjectRetention(
  bucket: string,
  payload: ObjectRetentionRequest,
): Promise<ObjectVersion> {
  const { data } = await http.put<ObjectVersion>(
    `/objects/buckets/${encodeURIComponent(bucket)}/objects/retention`,
    payload,
  );
  return data;
}

export async function setObjectLegalHold(
  bucket: string,
  payload: ObjectLegalHoldRequest,
): Promise<ObjectVersion> {
  const { data } = await http.put<ObjectVersion>(
    `/objects/buckets/${encodeURIComponent(bucket)}/objects/legal-hold`,
    payload,
  );
  return data;
}

export async function presignObject(
  bucket: string,
  payload: PresignRequest,
): Promise<PresignedUrl> {
  const { data } = await http.post<PresignedUrl>(
    `/objects/buckets/${encodeURIComponent(bucket)}/presign`,
    payload,
  );
  return data;
}

// --- Multipart upload (large files) ---
// The backend initiates the S3 multipart upload and returns a presigned PUT
// URL per part; the browser uploads each part directly to object storage,
// then asks the backend to assemble (or abort) the upload.

export async function initMultipart(
  bucket: string,
  payload: MultipartInitRequest,
): Promise<MultipartUpload> {
  const { data } = await http.post<MultipartUpload>(
    `/objects/buckets/${encodeURIComponent(bucket)}/multipart`,
    payload,
  );
  return data;
}

export async function completeMultipart(
  bucket: string,
  uploadId: string,
  payload: MultipartCompleteRequest,
): Promise<MultipartCompleteResult> {
  const { data } = await http.post<MultipartCompleteResult>(
    `/objects/buckets/${encodeURIComponent(bucket)}/multipart/${encodeURIComponent(uploadId)}/complete`,
    payload,
  );
  return data;
}

export async function abortMultipart(
  bucket: string,
  uploadId: string,
  key: string,
): Promise<void> {
  await http.delete(
    `/objects/buckets/${encodeURIComponent(bucket)}/multipart/${encodeURIComponent(uploadId)}`,
    { params: { key } },
  );
}

export async function downloadObject(
  bucket: string,
  key: string,
): Promise<void> {
  const response = await http.get(
    `/objects/buckets/${encodeURIComponent(bucket)}/objects/${encodeKey(key)}/download`,
    { responseType: "blob" },
  );
  const url = window.URL.createObjectURL(response.data as Blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = key.split("/").pop() ?? key;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// --- Events: rules that publish a bucket's events to a notification ---
export async function listBucketEventRules(bucket: string): Promise<BucketEventRules> {
  const { data } = await http.get<BucketEventRules>(
    `/objects/buckets/${encodeURIComponent(bucket)}/event-rules`,
  );
  return data;
}

export async function createBucketEventRule(
  bucket: string,
  payload: BucketEventRuleCreateRequest,
): Promise<BucketEventRule> {
  const { data } = await http.post<BucketEventRule>(
    `/objects/buckets/${encodeURIComponent(bucket)}/event-rules`,
    payload,
  );
  return data;
}

export async function updateBucketEventRule(
  bucket: string,
  ruleId: string,
  payload: BucketEventRuleUpdateRequest,
): Promise<BucketEventRule> {
  const { data } = await http.put<BucketEventRule>(
    `/objects/buckets/${encodeURIComponent(bucket)}/event-rules/${encodeURIComponent(ruleId)}`,
    payload,
  );
  return data;
}

export async function deleteBucketEventRule(bucket: string, ruleId: string): Promise<void> {
  await http.delete(
    `/objects/buckets/${encodeURIComponent(bucket)}/event-rules/${encodeURIComponent(ruleId)}`,
  );
}

export async function sendBucketTestEvent(
  bucket: string,
  ruleId: string,
): Promise<BucketEventTestResult> {
  const { data } = await http.post<BucketEventTestResult>(
    `/objects/buckets/${encodeURIComponent(bucket)}/event-rules/${encodeURIComponent(ruleId)}/test`,
  );
  return data;
}

// --- Browser access rules (CORS) ---
// Answered by the object store's HTTPS address, not by the store itself.

export async function getBucketCors(bucket: string): Promise<BucketCors> {
  const { data } = await http.get<BucketCors>(
    `/objects/buckets/${encodeURIComponent(bucket)}/cors`,
  );
  return data;
}

export async function setBucketCors(
  bucket: string,
  rules: BucketCorsRule[],
): Promise<BucketCors> {
  const { data } = await http.put<BucketCors>(
    `/objects/buckets/${encodeURIComponent(bucket)}/cors`,
    { rules },
  );
  return data;
}

export async function deleteBucketCors(bucket: string): Promise<void> {
  await http.delete(`/objects/buckets/${encodeURIComponent(bucket)}/cors`);
}
